import React, { CSSProperties } from "react";

import { FieldPath, FieldValues } from "react-hook-form";
import { SwitchProps } from "@mui/material";

import ControllerInput, { ControllerInputProps } from "@/components/atoms/Form/ControllerInput/ControllerInput";

import Switch from "../Switch/Switch";

export type SwitchInputProps<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<ControllerInputProps<TFieldValues, TName>, "render" | "hideErrorMessage"> & Omit<SwitchProps, "form">;

const FormControlStyle: CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    flexDirection: "row",
    padding: "8px 0",
    gap: "8px",
    justifyContent: "space-between",
};

const SwitchInput = <
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
    name,
    control,
    type,
    defaultValue = null,
    FormControlProps,
    LabelProps,
    label,
    fullWidth,
    required = false,
    className,
    ...switchInputProps
}: SwitchInputProps<TFieldValues, TName>): React.ReactElement => {
    return (
        <ControllerInput
            control={control}
            name={name}
            defaultValue={defaultValue}
            label={label}
            LabelProps={LabelProps}
            FormControlProps={{ ...FormControlProps, style: { ...FormControlStyle, ...FormControlProps?.style } }}
            required={required}
            fullWidth={fullWidth}
            className={className}
            hideErrorMessage
            render={({ field: { onChange, value } }) => (
                <Switch
                    onChange={(e) => onChange(e.target.checked)}
                    checked={value}
                    value={value}
                    inputProps={{
                        id: name,
                        name,
                        type: "checkbox",
                        checked: value,
                        // Necessary, https://github.com/mui/material-ui/issues/34154 then remove
                        // @ts-ignore
                        "data-testid": `${name}_switch-input`,
                    }}
                    {...switchInputProps}
                />
            )}
        />
    );
};

export default SwitchInput;
